import styled, {css} from "styled-components";
import {Functions} from "../../../../utils/function/Functions";
import {FlexCenterStyle, fontSize, fontWeight, VerticalFlexStyle} from "../../styles/styles";
import CheckMarkIcon from "../../icons/check.svg?react";
import {colorPalette} from "../../styles/colorPalette";

interface IStepIndicatorProps {
	readonly steps: string[];
	readonly currentStepIndex: number;
	readonly customWidth?: number;
	readonly onStepClick?: (stepIndex: number) => void;
}

export const StepIndicatorV5 = (props: IStepIndicatorProps) => {
	const {steps, currentStepIndex, customWidth, onStepClick = Functions.emptyFunction} = props;

	return (
		<StepIndicatorStyled
			$donePercent={(currentStepIndex / (steps.length - 1)) * 100}
			$customWidth={customWidth}
		>
			{steps.map((value: string, index: number) => {
				const isDone = index < currentStepIndex;

				return (
					<StepIndicatorStepStyled
						key={value}
						data-content={value}
						$cursor={onStepClick !== Functions.emptyFunction}
						$active={index === currentStepIndex}
						$isDone={isDone}
						onClick={() => onStepClick(index)}
					>
						{index === currentStepIndex && <span />}
						{isDone && <CheckMarkIcon />}
						<h3>{steps[index]}</h3>
					</StepIndicatorStepStyled>
				);
			})}
		</StepIndicatorStyled>
	);
};

const StepIndicatorStepStyled = styled.div<{$cursor: boolean; $active: boolean; $isDone: boolean}>`
	${VerticalFlexStyle};
	color: ${colorPalette.white};
	width: 32px;
	height: 32px;
	border: solid 2px currentColor;
	border: solid 2px ${(props) => (props.$active || props.$isDone ? colorPalette.primary.c500Primary : colorPalette.gray.c300)};
	border-radius: 50%;
	background-color: ${(props) => (props.$isDone ? colorPalette.primary.c500Primary : colorPalette.white)};
	align-items: center;
	justify-content: center;

	svg {
		stroke: currentColor;
	}

	span {
		width: 10px;
		height: 10px;
		background-color: ${colorPalette.primary.c500Primary};
		border-radius: 50%;
	}

	h3 {
		font-size: ${fontSize.xl};
		font-family: Roboto, sans-serif;
		font-weight: ${fontWeight.normal};
		width: max-content;
		position: absolute;
		top: 40px;
	}

	${(props) => {
		if (props.$cursor) {
			return css`
				cursor: pointer;
			`;
		}

		if (props.$active || props.$isDone) {
			return css`
				color: ${colorPalette.primary.c500Primary};
			`;
		}
	}}

	${(props) => {
		if (props.$isDone) {
			return css`
				background-color: ${colorPalette.primary.c500Primary};
				color: ${colorPalette.white};
				border-color: ${colorPalette.primary.c500Primary};
			`;
		}
	}}
`;

export const StepIndicatorStyled = styled.div<{$donePercent: number; $customWidth: number}>`
	${FlexCenterStyle};
	justify-content: space-between;
	position: relative;
	width: ${(props) => (!!props.$customWidth ? `${props.$customWidth}px` : "600px")};
	align-self: center;
	padding-bottom: 50px;
	z-index: 2;

	&::after {
		position: absolute;
		content: "";
		width: 95%;
		height: 2px;
		background-color: ${colorPalette.gray.c300};
		z-index: -2;
	}

	&::before {
		position: absolute;
		content: "";
		background-color: ${colorPalette.primary.c500Primary};
		height: 2px;
		z-index: -1;
		transition: cubic-bezier(0, 0, 0.2, 1) width 0.4s;
		${(props) => css`
			width: ${props.$donePercent}%;
		`};
	}

	${StepIndicatorStepStyled} {
		h3 {
			color: ${colorPalette.gray.c700Dark};
		}
	}
`;
